import {
  ArrowRight,
  Building2,
  CalendarDays,
  ClipboardCheck,
  Star,
  Timer,
  UsersRound,
} from 'lucide-react';
import type { ReactNode } from 'react';
import { Link } from 'react-router';

import { AdminPage } from '@/components/admin/AdminPage';
import { EmptyState, ErrorState } from '@/components/ui/empty-state';
import { KpiCard } from '@/components/ui/kpi-card';
import { StatusBadge } from '@/components/ui/status-badge';
import { useDashboard } from '@/hooks/dashboard';
import { JOB_STATUS_TONES } from '@/hooks/jobs';
import { useSession } from '@/lib/auth-client';
import { formatDay, formatInstant, formatWallTime } from '@/lib/dates';
import type { AdminDashboard } from '@shared/schemas/dashboard';

/**
 * The admin dashboard — ROADMAP.md §10.6.
 *
 * **This screen has been promising numbers since slice 2.4, and now has them.**
 * Its four cards said "Not summarised here yet" — honest while nothing counted
 * anything, and dishonest the moment something does. They are the **same four**,
 * so the promise that gets kept is the one that was made: Jobs today, Completed
 * this week, Active employees, Active clients.
 *
 * **A zero is `0`.** P1 and P10, and the rule the whole slice turns on: a
 * morning with nothing booked shows `0` jobs today, which is a fact somebody can
 * act on rather than a question about whether the screen is broken. `KpiCard`
 * cannot be handed a dash — `value` is `number | null` — and every count in
 * `AdminDashboard` is a plain number, so there is no path to one.
 *
 * **One fetch for nine things**, because `request-budget.spec.ts` asserts no
 * admin screen asks for the same endpoint twice, and a dashboard built from nine
 * hooks is the shape that eventually breaks that.
 *
 * **No build-phase numbers anywhere** — owner feedback, 2026-08-05. "Phase 10"
 * is our vocabulary and this is the first screen the business sees.
 */
export function DashboardPage() {
  const { data: session } = useSession();
  const firstName = session?.user.name.split(' ')[0] ?? 'there';

  const dashboard = useDashboard();

  return (
    <AdminPage
      title={`Good to see you, ${firstName}`}
      description="Honeycomb Cleaning Services, Cedar City."
    >
      {dashboard.error ? (
        <div className="rounded-lg border border-ink-300 bg-white shadow-card">
          {/* A failed request is an error, never an empty dashboard — P22. */}
          <ErrorState
            onRetry={() => {
              void dashboard.refetch();
            }}
          />
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
            <KpiCard
              label="Jobs today"
              value={dashboard.data?.kpis.jobsToday ?? null}
              isLoading={dashboard.isPending}
              icon={CalendarDays}
              subLabel={dashboard.data ? formatDay(dashboard.data.today.date) : undefined}
            />
            <KpiCard
              label="Completed this week"
              value={dashboard.data?.kpis.completedThisWeek ?? null}
              isLoading={dashboard.isPending}
              icon={ClipboardCheck}
              subLabel="Since Sunday"
            />
            <KpiCard
              label="Active employees"
              value={dashboard.data?.kpis.activeEmployees ?? null}
              isLoading={dashboard.isPending}
              icon={UsersRound}
            />
            <KpiCard
              label="Active clients"
              value={dashboard.data?.kpis.activeClients ?? null}
              isLoading={dashboard.isPending}
              icon={Building2}
            />
            {/*
              **Where the crew is, right now.** Distinct from "Shifts never
              clocked out" in Waiting for you below: that one is a problem to go
              and fix, this is simply who is working. Both read the same column
              and answer different questions.
            */}
            <KpiCard
              label="On the clock"
              value={dashboard.data?.kpis.onTheClock ?? null}
              isLoading={dashboard.isPending}
              icon={Timer}
              subLabel="Clocked in right now"
            />
          </div>

          {dashboard.isPending ? (
            <div aria-busy="true" className="mt-6">
              <span className="sr-only">Loading the dashboard</span>
              <div aria-hidden="true" className="h-72 animate-pulse rounded-lg bg-ink-100" />
            </div>
          ) : dashboard.data ? (
            <Sections data={dashboard.data} />
          ) : null}
        </>
      )}
    </AdminPage>
  );
}

function Sections({ data }: { data: AdminDashboard }) {
  return (
    <>
      <div className="mt-6 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Attention data={data} />
        <Today data={data} />
      </div>

      <div className="mt-4 grid grid-cols-1 gap-4 lg:grid-cols-2">
        <NotScheduled data={data} />
        <HighPriority data={data} />
      </div>

      <div className="mt-4">
        <Activity data={data} />
      </div>
    </>
  );
}

function Panel({
  title,
  action,
  children,
}: {
  title: string;
  action?: { label: string; to: string };
  children: ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-ink-300 bg-white shadow-card">
      <div className="flex items-center justify-between gap-3 border-b border-ink-300 px-4 py-3">
        <h2 className="text-h3 text-ink-900">{title}</h2>
        {action ? (
          <Link
            to={action.to}
            className="inline-flex min-h-touch items-center gap-1 text-body text-bark-700 underline"
          >
            {action.label}
            <ArrowRight aria-hidden="true" className="size-4" />
          </Link>
        ) : null}
      </div>
      {children}
    </section>
  );
}

/**
 * Everything waiting for somebody, with the link that answers it.
 *
 * **Rows with a count of zero stay.** A list that hides its empty entries cannot
 * tell you nothing is waiting — it only looks shorter, and "is there anything in
 * the leads inbox" is exactly the question this answers. Zero is `0`.
 */
function Attention({ data }: { data: AdminDashboard }) {
  const waiting = data.attention.filter((entry) => entry.isUrgent).length;

  return (
    <Panel title="Waiting for you">
      {waiting === 0 ? (
        <p className="border-b border-ink-100 px-4 py-3 text-body text-ink-500">
          Nothing is waiting — every queue below is empty.
        </p>
      ) : null}

      <ul className="divide-y divide-ink-100">
        {data.attention.map((entry) => (
          <li key={entry.key}>
            <Link
              to={entry.href}
              className="flex min-h-touch items-center justify-between gap-3 px-4 py-2.5 hover:bg-honey-50"
            >
              <span
                className={entry.isUrgent ? 'text-body text-ink-900' : 'text-body text-ink-500'}
              >
                {entry.label}
              </span>
              <span
                className={
                  entry.isUrgent
                    ? 'min-w-8 rounded-full bg-honey-400 px-2 py-0.5 text-center text-label tabular-nums text-ink-900'
                    : 'min-w-8 px-2 py-0.5 text-center text-label tabular-nums text-ink-500'
                }
              >
                {entry.count}
              </span>
            </Link>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

function Today({ data }: { data: AdminDashboard }) {
  const { today } = data;
  const percent = today.total === 0 ? 0 : Math.round((today.completed / today.total) * 100);

  return (
    <Panel title="Today" action={{ label: 'Calendar', to: '/admin/calendar' }}>
      {today.total === 0 ? (
        <p className="px-4 py-4 text-body text-ink-500">
          {/* An answer, not an empty state — nothing booked is a real fact. */}
          No jobs booked for {formatDay(today.date)}.
        </p>
      ) : (
        <div className="px-4 py-4">
          <div className="flex items-baseline justify-between">
            <p className="text-body text-ink-900">
              {today.completed} of {today.total} finished
            </p>
            <p className="text-meta tabular-nums text-ink-500">{percent}%</p>
          </div>

          {/*
            `role="progressbar"` carrying its values, because a coloured bar is
            nothing at all to a screen reader and the figure beside it is the
            whole of the content.
          */}
          <div
            role="progressbar"
            aria-valuenow={today.completed}
            aria-valuemin={0}
            aria-valuemax={today.total}
            aria-label="Jobs finished today"
            className="mt-2 h-2 overflow-hidden rounded-full bg-ink-100"
          >
            <div className="h-full rounded-full bg-honey-400" style={{ width: `${percent}%` }} />
          </div>

          <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            {today.byStatus.map((entry) => (
              <li key={entry.status} className="text-meta text-ink-500">
                {/* The label, never the stored value — P5. */}
                {entry.label} <span className="tabular-nums text-ink-900">{entry.count}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </Panel>
  );
}

function NotScheduled({ data }: { data: AdminDashboard }) {
  return (
    <Panel title="Not scheduled yet" action={{ label: 'Jobs', to: '/admin/jobs' }}>
      {data.pendingAssignment.length === 0 ? (
        <p className="px-4 py-4 text-body text-ink-500">Every job has a date and a crew.</p>
      ) : (
        <ul className="divide-y divide-ink-100">
          {data.pendingAssignment.map((entry) => (
            <li key={entry.id}>
              <Link
                to={`/admin/jobs/${entry.id}`}
                className="block min-h-touch px-4 py-2.5 hover:bg-honey-50"
              >
                <p className="text-body text-ink-900">
                  {entry.clientName}
                  <span className="ml-2 text-meta text-ink-500">{entry.jobNumber}</span>
                </p>
                <p className="text-meta text-ink-500">
                  {entry.serviceName} · {formatDay(entry.scheduledDate)} at{' '}
                  {formatWallTime(entry.scheduledTime)}
                </p>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function HighPriority({ data }: { data: AdminDashboard }) {
  return (
    <Panel title="High priority clients" action={{ label: 'Clients', to: '/admin/clients' }}>
      {data.highPriorityClients.length === 0 ? (
        <p className="px-4 py-4 text-body text-ink-500">
          {/*
            Explains the flag rather than only reporting its absence — it is what
            gates calendar sync (business-rules.md §6), and somebody reading this
            may not know that.
          */}
          Nobody is flagged high priority. The flag is on a client&apos;s own page, and it is what
          turns on calendar sync for short-term rentals.
        </p>
      ) : (
        <ul className="divide-y divide-ink-100">
          {data.highPriorityClients.map((entry) => (
            <li key={entry.id}>
              <Link
                to={`/admin/clients/${entry.id}`}
                className="flex min-h-touch items-center justify-between gap-3 px-4 py-2.5 hover:bg-honey-50"
              >
                <span className="flex items-center gap-2 text-body text-ink-900">
                  <Star aria-hidden="true" className="size-4 text-bark-700" />
                  {entry.name}
                </span>
                <span className="text-meta text-ink-500">
                  {entry.nextJobDate === null
                    ? 'No clean booked'
                    : `Next ${formatDay(entry.nextJobDate)}`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

function Activity({ data }: { data: AdminDashboard }) {
  return (
    <Panel title="Recent activity">
      {data.activity.length === 0 ? (
        <EmptyState
          icon={CalendarDays}
          title="Nothing has happened yet."
          hint="Changes to jobs show up here as they are made."
        />
      ) : (
        <ul className="divide-y divide-ink-100">
          {data.activity.map((entry) => (
            <li key={entry.id}>
              <Link
                to={`/admin/jobs/${entry.jobId}`}
                className="flex min-h-touch flex-wrap items-center justify-between gap-2 px-4 py-2.5 hover:bg-honey-50"
              >
                <span className="flex flex-wrap items-center gap-2">
                  <StatusBadge label={entry.statusLabel} tone={JOB_STATUS_TONES[entry.status]} />
                  <span className="text-body text-ink-900">{entry.clientName}</span>
                  <span className="text-meta text-ink-500">{entry.jobNumber}</span>
                </span>
                <span className="text-meta text-ink-500">
                  {formatInstant(entry.changedAt)}
                  {/*
                    Null is the cron worker, which has no human behind it.
                    Saying so is more honest than a line that reads as though
                    somebody did it by hand.
                  */}
                  {entry.changedByName === null ? ' · automatic' : ` · ${entry.changedByName}`}
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}
